import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { RelatedToType } from '@shared/enums';
import { ApprovalProcess, ApprovalPanelItem, EligibleApprover } from './approvals.types';

@Injectable({ providedIn: 'root' })
export class ApprovalsService {
  private readonly http = inject(HttpClient);

  getEligibleApprovers(relatedToId: number, relatedToType: RelatedToType): Observable<EligibleApprover[]> {
    const params = new HttpParams().set('related_to_id', String(relatedToId)).set('related_to_type', relatedToType);

    return this.http
      .get<{ eligible_approvers: EligibleApprover[] }>('approval_processes/eligible_approvers', { params })
      .pipe(map(({ eligible_approvers }) => eligible_approvers));
  }

  getApprovalById(id: number, relatedToId: number, relatedToType: RelatedToType): Observable<ApprovalProcess> {
    const params = new HttpParams().set('related_to_id', String(relatedToId)).set('related_to_type', relatedToType);
    return this.http
      .get<{ approval_process: ApprovalProcess }>(`approval_processes/${id}`, { params })
      .pipe(map(({ approval_process }) => approval_process));
  }

  getApprovals(relatedToId: number, relatedToType: RelatedToType): Observable<ApprovalProcess[]> {
    const params = new HttpParams().set('related_to_id', String(relatedToId)).set('related_to_type', relatedToType);

    return this.http
      .get<{ approval_processes: ApprovalProcess[] }>('approval_processes', { params })
      .pipe(map(({ approval_processes }) => approval_processes));
  }

  createApproval(
    name: string,
    layout: ApprovalPanelItem[],
    relatedToId: number,
    relatedToType: RelatedToType,
    snapshot: Record<string, any> = {},
  ): Observable<ApprovalProcess> {
    return this.http
      .post<{ approval_process: ApprovalProcess }>('approval_processes', {
        approval_process: {
          related_to_type: relatedToType,
          related_to_id: relatedToId,
          name,
          snapshot,
          layout_definition: { tab: layout },
        },
      })
      .pipe(map(({ approval_process }) => approval_process));
  }

  deleteApproval(id: number, relatedToId: number, relatedToType: RelatedToType): Observable<void> {
    const params = new HttpParams().set('related_to_id', String(relatedToId)).set('related_to_type', relatedToType);
    return this.http.delete<void>(`approval_processes/${id}`, { params });
  }

  updateSnapshot(
    approvalId: number,
    snapshot: Record<string, any>,
    relatedToId: number,
    relatedToType: RelatedToType,
  ): Observable<ApprovalProcess> {
    return this.http
      .put<{ approval_process: ApprovalProcess }>(`approval_processes/${approvalId}`, {
        approval_process: { snapshot, related_to_id: relatedToId, related_to_type: relatedToType },
      })
      .pipe(map(({ approval_process }) => approval_process));
  }

  updateName(
    approvalId: number,
    name: string,
    relatedToId: number,
    relatedToType: RelatedToType,
  ): Observable<ApprovalProcess> {
    return this.http
      .put<{ approval_process: ApprovalProcess }>(`approval_processes/${approvalId}`, {
        approval_process: { name, related_to_id: relatedToId, related_to_type: relatedToType },
      })
      .pipe(map(({ approval_process }) => approval_process));
  }

  updateLayout(
    approvalId: number,
    layout: ApprovalPanelItem[],
    relatedToId: number,
    relatedToType: RelatedToType,
  ): Observable<ApprovalProcess> {
    return this.http
      .put<{ approval_process: ApprovalProcess }>(`approval_processes/${approvalId}`, {
        approval_process: {
          layout_definition: { tab: layout },
          related_to_id: relatedToId,
          related_to_type: relatedToType,
        },
      })
      .pipe(map(({ approval_process }) => approval_process));
  }

  sendApprovalRequest(
    approvalId: number,
    userIds: number[],
    relatedToId: number,
    relatedToType: RelatedToType,
  ): Observable<ApprovalProcess> {
    return this.http
      .post<{ approval_process: ApprovalProcess }>(`approval_processes/${approvalId}/request_approval`, {
        approval_process: {
          approver_ids: userIds,
          related_to_id: relatedToId,
          related_to_type: relatedToType,
        },
      })
      .pipe(map(({ approval_process }) => approval_process));
  }

  approveReview(approvalRowId: number, comments: string): Observable<void> {
    return this.http.put<void>(`approvals/${approvalRowId}/approve`, { approval: { comments } });
  }

  declineReview(approvalRowId: number, comments: string): Observable<void> {
    return this.http.put<void>(`approvals/${approvalRowId}/decline`, { approval: { comments } });
  }
}
